import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, useCart, useToast } from '@kss/ui';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { useAuth } from '@kss/auth';
import { publicCatalogService, type Product as ApiProduct } from '@kss/api';
import { adaptProduct } from '../shop/productAdapter';
import { toAbsoluteImageUrl } from '../utils/imageUrl';
import { useWishlist } from './wishlistStorage';

/**
 * Customer-facing "saved for later" page.
 *
 * Persistence is per-user `localStorage` (see `wishlistStorage.ts`)
 * until backend Item 4 ships. Products are pulled lazily from the
 * public catalog so the page works for anonymous reads — same flow
 * the order-details page uses for the Reorder action.
 */
export default function AccountWishlist() {
  const { user } = useAuth();
  const cart = useCart();
  const toast = useToast();
  const wishlist = useWishlist(user?.id);

  const [productById, setProductById] = useState<Record<string, ApiProduct>>({});
  const [loading, setLoading] = useState(false);

  // Fetch every saved product on mount and whenever the entry list
  // changes. Uses `Promise.allSettled` so one stale productId
  // (deleted from the catalog) doesn't blank the rest of the page.
  useEffect(() => {
    const ids = wishlist.entries.map((e) => e.productId);
    if (ids.length === 0) {
      setProductById({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const results = await Promise.allSettled(
        ids.map((id) => publicCatalogService.productById(id)),
      );
      if (cancelled) return;
      const map: Record<string, ApiProduct> = {};
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value?.productId) {
          map[r.value.productId] = r.value;
        }
      }
      setProductById(map);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [wishlist.entries]);

  function addOneToCart(p: ApiProduct) {
    const ui = adaptProduct(p, {});
    cart.add(ui, 1);
    toast.success('Added to cart', ui.title);
  }

  function moveAllToCart() {
    let added = 0;
    for (const e of wishlist.entries) {
      const p = productById[e.productId];
      if (!p) continue;
      cart.add(adaptProduct(p, {}), 1);
      added += 1;
    }
    if (added > 0) {
      toast.success(
        'Cart updated',
        `Added ${added} ${added === 1 ? 'item' : 'items'} from your wishlist.`,
      );
      cart.openDrawer();
    } else {
      toast.error(
        'Nothing added',
        'These products are no longer available.',
      );
    }
  }

  if (wishlist.entries.length === 0) {
    return (
      <div className="grid gap-3">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Wishlist</h1>
        <Card className="grid place-items-center gap-2 p-10 text-center">
          <Heart className="h-8 w-8 text-[var(--color-text-muted)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            Nothing saved yet
          </p>
          <p className="max-w-sm text-xs text-[var(--color-text-secondary)]">
            Tap the heart on any product to save it here for later.
          </p>
          <Link to="/shop" className="mt-2">
            <Button size="sm">Browse products</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Wishlist ({wishlist.entries.length})
        </h1>
        <Button size="sm" onClick={moveAllToCart} disabled={loading}>
          <ShoppingCart className="h-3.5 w-3.5" />
          Add all to cart
        </Button>
      </div>

      <ul className="grid gap-2">
        {wishlist.entries.map((entry) => {
          const p = productById[entry.productId];
          const title = p?.name?.trim() || p?.sku || entry.productId;
          const price = Number(p?.sellingPrice ?? 0);
          const image = toAbsoluteImageUrl(p?.imageUrl);
          const unavailable = !loading && !p;
          return (
            <li key={entry.productId}>
              <Card className="flex items-center gap-3 p-3">
                <Link
                  to={`/shop/${entry.productId}`}
                  className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-[var(--color-bg-card-hover)]"
                >
                  {image ? (
                    <img
                      src={image}
                      alt={title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl opacity-60">📦</span>
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/shop/${entry.productId}`}
                    className="block truncate text-sm font-medium text-[var(--color-text-primary)] hover:underline"
                  >
                    {title}
                  </Link>
                  {unavailable ? (
                    <p className="text-xs text-[var(--color-danger)]">
                      No longer available
                    </p>
                  ) : (
                    <p className="text-xs text-[var(--color-text-muted)]">
                      ₹{price.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {p && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addOneToCart(p)}
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                      Add
                    </Button>
                  )}
                  <button
                    type="button"
                    onClick={() => wishlist.remove(entry.productId)}
                    aria-label={`Remove ${title} from wishlist`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
